import {CommonModule} from '@angular/common';
import {ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatDividerModule} from '@angular/material/divider';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSelectModule} from '@angular/material/select';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {Procedure} from '../../../../models/procedures/ProcedureResponse';
import {debounceTime, distinctUntilChanged, merge, startWith, Subscription, tap} from 'rxjs';
import {ProceduresService} from '../../../../services/procedures/procedures.service';
import {Params} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {DocumentViewDialogComponent} from '../../create-procedure/document-view-dialog/document-view-dialog.component';
import {BreadcrumbComponent} from '../../../breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-liste-documents',
  standalone: true,
  imports: [MatTableModule, MatIconModule, MatSortModule, MatPaginatorModule, MatInputModule,
    MatCardModule, MatDividerModule, MatSelectModule, CommonModule,
    ReactiveFormsModule, FormsModule, MatFormFieldModule, BreadcrumbComponent
  ],
  templateUrl: './liste-documents.component.html',
  styleUrl: './liste-documents.component.scss'
})
export class ListeDocumentsComponent {
  displayedColumns = ['mnemonique', 'titre', 'createdAt', 'fileName', 'actions'];
  dataSource = new MatTableDataSource<Procedure>();
  filterGeneral = '';
  procedure: Procedure | undefined;
  subscriptions: Subscription[] = [];

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  filter = this._formBuilder.group({
    general_filter: new FormControl('', [])
});

  constructor(
    private proceduresService: ProceduresService,
    private _formBuilder: FormBuilder,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    if (this.sort && this.paginator) {

      this.sort.active = 'createdAt';
      this.sort.direction = 'desc';
      this.subscriptions.push(
        this.sort.sortChange.subscribe(() => (this.paginator!.pageIndex = 0))
      )

      this.subscriptions.push(
        merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          startWith(null),
          tap(() => this.loadProcedures())
          )
        .subscribe()
      )
    }
    else {
      this.loadProcedures();
    }

    this.subscriptions.push(
      this.filter.controls.general_filter.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((searchText) => {
          this.filterGeneral = searchText || '';
          this.paginator!.pageIndex = 0;
          this.loadProcedures();
        })
      ).subscribe()
    )
  }

  loadProcedures() {
    if (this.paginator && this.sort) {
      const params: Params = {
        page: this.paginator.pageIndex + 1,
        limit: this.paginator.pageSize,
        search: this.filterGeneral
      };

      if (this.sort.active && this.sort.direction !== '') {
        params['orderBy'] = this.sort.active;
        params['direction'] = this.sort.direction;
      }

      this.subscriptions.push(
        this.proceduresService.getProcedures(params).subscribe({
          next: (procedures) => {
            const procedureWithAnnexe = procedures.data.filter(p => p.annexes && p.annexes.length > 0)
            this.dataSource.data = procedureWithAnnexe;
            this.paginator!.length = procedureWithAnnexe.length;
          },
          error: (error) => {
            console.error('Erreur lors de la récupération des documents', error);
          }
        })
      )
    }
  }

  openDocumentViewDialog(procedure: Procedure): void {
    this.subscriptions.push(
      this.proceduresService.getProcedureById(procedure.id).subscribe({
        next: (procedureResponse) => {
          this.procedure = procedureResponse.data;
          this.cdr.markForCheck();
          const annexe = procedureResponse.data.annexes[0];

          this.dialog.open(DocumentViewDialogComponent, {
            width: '80%',
            height: '80%',
            data: { annexe }
          });

        },
        error: (error) => {
          console.error('Error fetching procedure', error);
        }
      })
    );
  }

  clearGeneralFilter() {
    this.filter.controls['general_filter'].setValue('');
    this.loadProcedures();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }
}
